/* liftsets (BUILD-xliii editorial): behaviour for the guide article system. Loaded only by the
   guide-* sections; nothing runs on pages without the hooks.
   1. Running outline (sections/guide-body): IntersectionObserver on the body's h2[id] plus a
      rAF-throttled position check sets aria-current="true" on exactly one outline link; clicks
      scroll smoothly (unless prefers-reduced-motion) and replace the hash.
   2. Tables: marks each wrapper that overflows (fade + "Swipe" hint), clears the fade at the end
      of the scroll, zebra-stripes tables with more than five rows, labels the region by its caption.
   3. Mobile shop bar (sections/guide-sticky-cta): shown after the hero has scrolled out, hidden
      while the build-your-set tool or the end matter is on screen; the cookie banner rule is CSS
      (html.ls-consent-open). html.ls-guide-shopbar-open reserves the bar's 56 px on the body.
   4. Build your set (snippets/guide-tool-set): re-runs the rule table from data-* attributes on
      every change, re-renders rows from the product JSON, keeps items[i][id] in sync with the
      no-JS POST, submits ONE /cart/add.js call and re-renders Dawn's cart drawer/notification.
   5. Share: copy-link button (Clipboard API) with a text status; WhatsApp / X are plain links. */
(function () {
  'use strict';
  if (window.__lsGuideLoaded) return;
  window.__lsGuideLoaded = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(cents) {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
  function kgLabel(opt) {
    return String(opt).replace(/kg$/, '').replace(/\.0$/, '');
  }
  function parseMap(str) {
    var out = {};
    String(str || '').split(';').forEach(function (pair) {
      var i = pair.indexOf('=');
      if (i > 0) out[pair.slice(0, i)] = pair.slice(i + 1);
    });
    return out;
  }
  function headerH() {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return isNaN(v) ? 64 : v;
  }
  function onScroll(fn) {
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(function () { ticking = false; fn(); }); }
    }, { passive: true });
  }

  /* ---------- 1. running outline ---------- */
  function initOutline() {
    var nav = document.querySelector('nav[aria-label="On this page"]');
    var body = document.querySelector('.ls-guide__body');
    if (!nav || !body) return;
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    var heads = Array.prototype.slice.call(body.querySelectorAll('h2[id]'));
    if (!links.length || !heads.length) return;
    var byId = {};
    links.forEach(function (a) { byId[decodeURIComponent(a.getAttribute('href').slice(1))] = a; });
    var current = null;
    function setActive(id) {
      if (id === current) return;
      current = id;
      links.forEach(function (a) {
        if (byId[id] === a) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    }
    function update() {
      var line = headerH() + window.innerHeight * 0.3;
      var active = heads[0];
      for (var i = 0; i < heads.length; i++) {
        if (heads[i].getBoundingClientRect().top <= line) active = heads[i];
        else break;
      }
      /* at the very end of the page the last heading may never reach the line */
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) active = heads[heads.length - 1];
      setActive(active.id);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function () { update(); }, { rootMargin: '-20% 0px -70% 0px', threshold: [0, 0.15, 0.4] });
      heads.forEach(function (h) { io.observe(h); });
    }
    onScroll(update);
    window.addEventListener('resize', update);
    update();
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = decodeURIComponent(a.getAttribute('href').slice(1));
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + id);
        setActive(id);
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
    /* mobile <details>: close after a jump so the list does not sit over the section */
    var toc = document.querySelector('details.ls-guide__toc');
    if (toc) {
      toc.querySelectorAll('a[href^="#"]').forEach(function (a) {
        a.addEventListener('click', function () {
          var id = decodeURIComponent(a.getAttribute('href').slice(1));
          var target = document.getElementById(id);
          if (!target) return;
          if (window.matchMedia('(max-width: 767px)').matches) toc.open = false;
          target.setAttribute('tabindex', '-1');
          window.setTimeout(function () { target.focus({ preventScroll: true }); }, reduced ? 0 : 350);
        });
      });
    }
  }

  /* ---------- 2. tables ---------- */
  function initTables() {
    var wraps = document.querySelectorAll('.ls-guide__tablewrap');
    if (!wraps.length) return;
    var n = 0;
    wraps.forEach(function (wrap) {
      var region = wrap.querySelector('.ls-guide__table');
      var table = wrap.querySelector('table');
      if (!region || !table) return;
      var caption = table.querySelector('caption');
      if (caption) {
        if (!caption.id) caption.id = 'ls-guide-caption-' + (++n);
        region.setAttribute('aria-labelledby', caption.id);
        region.removeAttribute('aria-label');
      }
      if (table.querySelectorAll('tbody tr').length > 5) region.classList.add('is-zebra');
      function check() {
        var over = table.scrollWidth > region.clientWidth + 1;
        wrap.classList.toggle('is-overflowing', over);
        wrap.classList.toggle('is-scrolled-end', over && region.scrollLeft + region.clientWidth >= table.scrollWidth - 1);
      }
      region.addEventListener('scroll', check, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(check).observe(region);
      window.addEventListener('resize', check);
      check();
    });
  }

  /* ---------- 3. mobile shop bar ---------- */
  function initShopBar() {
    var bar = document.querySelector('[data-guide-shopbar]');
    if (!bar) return;
    var hero = document.querySelector('.ls-tldr') || document.querySelector('[data-guide-hero-end]') || document.querySelector('[data-guide-hero]') || document.querySelector('h1');
    var avoid = Array.prototype.slice.call(document.querySelectorAll('[data-guide-byos], [data-guide-end]'));
    var mq = window.matchMedia('(min-width: 1200px)');
    var pastHero = !hero;
    var covered = new Map();
    function blocked() {
      var n = 0;
      covered.forEach(function (v) { if (v) n += 1; });
      return n > 0;
    }
    function apply() {
      var show = !mq.matches && pastHero && !blocked() && !document.documentElement.classList.contains('ls-consent-open');
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.documentElement.classList.toggle('ls-guide-shopbar-open', show);
    }
    function checkHero() {
      var next = hero ? hero.getBoundingClientRect().bottom <= 0 : true;
      if (next !== pastHero) { pastHero = next; apply(); }
    }
    onScroll(checkHero);
    if ('IntersectionObserver' in window && avoid.length) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { covered.set(en.target, en.isIntersecting); });
        apply();
      }, { threshold: 0 });
      avoid.forEach(function (el) { io.observe(el); });
    }
    var mo = new MutationObserver(apply);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (mq.addEventListener) mq.addEventListener('change', apply);
    checkHero();
    apply();
  }

  /* ---------- 3b. shop bar follows the kit/set in view (v3.1) ---------- */
  function initOfferBar() {
    var bar = document.querySelector('[data-guide-shopbar]');
    if (!bar) return;
    var textEl = bar.querySelector('[data-guide-shopbar-text]');
    var btn = bar.querySelector('[data-guide-shopbar-button]');
    var offerBtn = bar.querySelector('[data-guide-shopbar-offer]');
    if (!textEl || !btn || !offerBtn) return;
    var offers = Array.prototype.slice.call(document.querySelectorAll('[data-guide-offer]'));
    if (!offers.length || !('IntersectionObserver' in window)) return;
    var current = null, visible = new Map(), buttonsOnScreen = new Map();
    var defaultText = textEl.innerHTML;
    function money(p) { return '£' + (p / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    function render() {
      var el = current;
      var btnVisible = el && buttonsOnScreen.get(el);
      if (el && !btnVisible) {
        var title = el.getAttribute('data-guide-offer') || '';
        var price = parseInt(el.getAttribute('data-guide-offer-price'), 10) || 0;
        var n = parseInt(el.getAttribute('data-guide-offer-count'), 10) || 0;
        textEl.innerHTML = '<strong>' + title.replace(/</g, '&lt;') + '</strong>' + (price ? ' · ' + money(price) : '');
        offerBtn.textContent = n > 1 ? 'Add ' + n + ' pairs' : 'Add to cart';
        offerBtn.hidden = false; btn.hidden = true;
        bar.classList.add('ls-guide__shopbar--offer');
      } else {
        textEl.innerHTML = defaultText; offerBtn.hidden = true; btn.hidden = false;
        bar.classList.remove('ls-guide__shopbar--offer');
      }
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        visible.set(en.target, en.intersectionRatio);
        if (en.intersectionRatio >= 0.5) current = en.target;
        else if (en.target === current && en.boundingClientRect.bottom < 0) { /* scrolled past: keep as the last offer */ }
        else if (en.target === current && en.boundingClientRect.top > window.innerHeight) current = null;
      });
      render();
    }, { threshold: [0, 0.5, 1] });
    offers.forEach(function (o) { io.observe(o); });
    var io2 = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var offer = en.target.closest('[data-guide-offer]'); if (!offer) return;
        buttonsOnScreen.set(offer, en.isIntersecting);
      });
      render();
    }, { threshold: 0 });
    offers.forEach(function (o) { var b = o.querySelector('.ls-kit__button'); if (b) io2.observe(b); });
    offerBtn.addEventListener('click', function () {
      if (!current) return;
      var formId = current.getAttribute('data-guide-offer-form');
      var form = formId && document.getElementById(formId);
      if (form) { var submit = form.querySelector('button[type="submit"]'); if (submit) submit.click(); else form.requestSubmit(); }
    });
  }

  /* ---------- 4. build your set ---------- */
  function initBuildYourSet() {
    var root = document.querySelector('[data-guide-byos]');
    if (!root) return;
    var form = root.querySelector('[data-guide-byos-form]');
    var dataEl = root.querySelector('[data-guide-data]');
    var tpl = root.querySelector('[data-guide-row-template]');
    var list = root.querySelector('[data-guide-items]');
    var totalOut = root.querySelector('[data-guide-total]');
    var countOut = root.querySelector('[data-guide-count]');
    var summary = root.querySelector('[data-guide-summary]');
    var note = root.querySelector('[data-guide-note]');
    var rackRow = root.querySelector('[data-guide-extra-row="rack"]');
    var rackInput = root.querySelector('[data-guide-extra="rack"]');
    var rackName = root.querySelector('[data-guide-rack-name]');
    var rackPrice = root.querySelector('[data-guide-rack-price]');
    var altSet = root.querySelector('[data-guide-alt-set]');
    var altSaving = root.querySelector('[data-guide-alt-set-saving]');
    var altTrade = root.querySelector('[data-guide-alt-trade]');
    var errorOut = root.querySelector('[data-guide-error]');
    var submit = root.querySelector('[data-guide-submit]');
    if (!form || !dataEl || !tpl || !list) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var rules = parseMap(root.getAttribute('data-rules'));
    var couple = parseMap(root.getAttribute('data-couple'));
    var groupNew = root.getAttribute('data-group-new');
    var groupExp = root.getAttribute('data-group-exp');
    var jobs = ['Raises and arms', 'Presses and rows', 'Squats, hinges and carries', 'Second light pair'];

    function answer(name) {
      var el = form.querySelector('input[name="' + name + '"]:checked');
      return el ? el.value : '';
    }
    function recommend() {
      var exp = answer('experience') || 'new';
      var loads = answer('loads') || 'standard';
      var goal = answer('goal') || 'general';
      var who = answer('who') || 'one';
      var space = answer('space') || 'compact';
      var rec = { pairs: [], set: null, who: who, space: space, exp: exp };
      if (who === 'group') {
        rec.set = exp === 'new' ? groupNew : groupExp;
        return rec;
      }
      var key = exp + '|' + loads + '|' + goal;
      var triple = (rules[key] || rules['new|standard|general'] || '').split(',').filter(Boolean);
      if (who === 'couple' && triple.length) {
        var extra = couple[triple[0]];
        if (extra && triple.indexOf(extra) === -1) {
          triple = triple.concat([extra]).sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
        }
      }
      rec.pairs = triple;
      return rec;
    }
    function rowFor(opt, index, job, isSet) {
      var src = isSet ? data.sets[opt] : data.pairs[opt];
      if (!src) return null;
      var frag = tpl.content.cloneNode(true);
      var li = frag.querySelector('[data-guide-item]');
      var input = li.querySelector('input');
      input.name = 'items[' + index + '][id]';
      input.value = src.id;
      input.disabled = !src.available;
      var a = li.querySelector('a');
      a.href = src.url;
      var img = li.querySelector('img');
      if (src.img) { img.src = src.img; } else { img.remove(); }
      li.querySelector('.ls-byos__item-title').textContent = isSet ? opt : ('2 × ' + kgLabel(opt) + ' kg hex pair');
      li.querySelector('.ls-byos__item-job').textContent = job;
      li.querySelector('.ls-byos__item-price').textContent = src.available ? money(src.price) : 'Sold out';
      li.setAttribute('data-price', src.available ? src.price : 0);
      return li;
    }
    function render() {
      var rec = recommend();
      list.innerHTML = '';
      var base = 0;
      var n = 0;
      if (rec.set) {
        var srow = rowFor(rec.set, 0, 'Graded set, rack included', true);
        if (srow) { list.appendChild(srow); base += parseInt(srow.getAttribute('data-price'), 10) || 0; n += 1; }
      } else {
        rec.pairs.forEach(function (opt, i) {
          var ordered = rec.pairs.length === 4;
          var job = ordered ? (i === 0 ? 'Raises and arms (second lifter)' : i === 1 ? 'Raises and arms' : i === 2 ? 'Presses and rows' : 'Squats, hinges and carries') : jobs[i] || '';
          var row = rowFor(opt, i, job, false);
          if (row) { list.appendChild(row); base += parseInt(row.getAttribute('data-price'), 10) || 0; n += 1; }
        });
      }
      var pairsCount = rec.pairs.length;
      var showRack = !!rackRow && rec.space === 'rack' && !rec.set && pairsCount > 0;
      if (rackRow) {
        rackRow.hidden = !showRack;
        if (rackInput) {
          if (!showRack) rackInput.checked = false;
          var k = pairsCount >= 4 ? '4' : '3';
          var id = rackInput.getAttribute('data-id-' + k);
          var price = rackInput.getAttribute('data-price-' + k);
          var title = rackInput.getAttribute('data-title-' + k);
          if (id && price && title) {
            rackInput.value = id;
            rackInput.setAttribute('data-price', price);
            if (rackName) rackName.textContent = title;
            if (rackPrice) rackPrice.textContent = '(+' + money(parseInt(price, 10)) + ')';
          }
        }
      }
      if (note) {
        if (rec.set && n === 0) note.textContent = 'The graded set for a group is not available to add right now. Ask for a trade quote below, or pick pairs for one person.';
        else if (rec.set) note.textContent = 'A graded set arrives as one order with its rack, so the rack option is off. The listing states every pair in this set.';
        else if (rec.space === 'rack') note.textContent = pairsCount >= 4 ? 'Four pairs: the 5-tier tree leaves a tier free for the next pair.' : 'Three pairs fill the 3-tier tree; buy the rack for the pairs you will own in a year.';
        else note.textContent = 'Compact: three pairs stand on the floor under a bench or along a wall; no rack needed.';
      }
      if (summary) {
        var whoText = rec.who === 'couple' ? 'two people' : rec.who === 'group' ? 'clients or a small group' : 'one person';
        var expText = rec.exp === 'new' ? 'new to lifting' : rec.exp === 'returning' ? 'with some training behind them' : 'with a year or more of training';
        summary.textContent = (rec.set ? 'A graded set for ' : (pairsCount === 4 ? 'Four pairs for ' : 'Three pairs for ')) + whoText + ', ' + expText + '.';
      }
      if (altSet) altSet.hidden = !!rec.set;
      if (altTrade) altTrade.hidden = rec.who !== 'group';
      if (altSaving) {
        altSaving.hidden = true;
        altSaving.textContent = '';
        if (rec.set && data.setSavings && data.setSavings[rec.set] && data.setSavings[rec.set].setPrice > 0 && data.setSavings[rec.set].saving > 0) {
          var s = data.setSavings[rec.set];
          altSaving.textContent = money(s.saving) + ' less than the same pairs plus the rack bought separately (' + money(s.pairsPlusRack) + ').';
          altSaving.hidden = false;
        }
      }
      if (totalOut) totalOut.setAttribute('data-base', String(base));
      if (countOut) countOut.setAttribute('data-base', String(n));
      updateTotal();
    }
    function updateTotal() {
      var base = parseInt((totalOut && totalOut.getAttribute('data-base')) || '0', 10);
      var n = parseInt((countOut && countOut.getAttribute('data-base')) || '0', 10);
      var sum = base;
      root.querySelectorAll('[data-guide-extra]').forEach(function (cb) {
        if (cb.checked && !cb.disabled && !cb.closest('[hidden]')) {
          sum += parseInt(cb.getAttribute('data-price') || '0', 10);
          n += 1;
        }
      });
      if (totalOut) totalOut.textContent = money(sum);
      if (countOut) countOut.textContent = String(n);
    }
    form.querySelectorAll('input[type="radio"]').forEach(function (r) { r.addEventListener('change', render); });
    root.querySelectorAll('[data-guide-extra]').forEach(function (cb) { cb.addEventListener('change', updateTotal); });
    updateTotal();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var items = [];
      form.querySelectorAll('input[name^="items["]').forEach(function (inp) {
        if (inp.disabled) return;
        if (inp.type === 'checkbox' && (!inp.checked || inp.closest('[hidden]'))) return;
        var id = parseInt(inp.value, 10);
        if (id > 0) items.push({ id: id, quantity: 1 });
      });
      if (!items.length) return;
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = { items: items };
      if (cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
        if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
      }
      if (errorOut) { errorOut.hidden = true; errorOut.textContent = ''; }
      if (submit) { submit.setAttribute('aria-disabled', 'true'); submit.classList.add('loading'); var sp = submit.querySelector('.loading__spinner'); if (sp) sp.classList.remove('hidden'); }
      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) {
            if (errorOut) { errorOut.textContent = res.description || res.message || 'Could not add to cart.'; errorOut.hidden = false; }
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: items[0].id, cartData: res });
          }
          if (cart && cart.renderContents) {
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (res.items && res.items.length && !res.id) {
              var main = res.items[0];
              Object.keys(main).forEach(function (k) { if (!(k in res)) res[k] = main[k]; });
            }
            cart.renderContents(res);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function () { window.location = (window.routes && window.routes.cart_url) || '/cart'; })
        .finally(function () {
          if (submit) { submit.removeAttribute('aria-disabled'); submit.classList.remove('loading'); var sp = submit.querySelector('.loading__spinner'); if (sp) sp.classList.add('hidden'); }
        });
    });
  }

  /* ---------- 5. share ---------- */
  function initShare() {
    var btn = document.querySelector('[data-guide-copy]');
    if (!btn) return;
    var status = document.querySelector('[data-guide-share-status]');
    btn.addEventListener('click', function () {
      var url = btn.getAttribute('data-guide-copy') || window.location.href;
      var done = function () { if (status) { status.textContent = 'Link copied'; window.setTimeout(function () { status.textContent = ''; }, 2500); } };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy this link', url); });
      else window.prompt('Copy this link', url);
    });
  }


  /* ---------- 6. multi-add forms (kit cards; BUILD-xlviii) ----------
     Any form[data-guide-multiadd] posts items[i][id] without JS; here it becomes one /cart/add.js call
     and Dawn's drawer/notification re-renders. Same pattern as the build-your-set submit. */
  function initMultiAdd() {
    var forms = document.querySelectorAll('form[data-guide-multiadd]');
    if (!forms.length) return;
    forms.forEach(function (form) {
      var submit = form.querySelector('button[type="submit"]');
      var errorOut = form.querySelector('[data-guide-multiadd-error]');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var items = [];
        form.querySelectorAll('input[name^="items["]').forEach(function (inp) {
          if (inp.disabled) return;
          var id = parseInt(inp.value, 10);
          var qtyInp = form.querySelector('input[name="' + inp.name.replace('[id]', '[quantity]') + '"]');
          var qty = qtyInp ? parseInt(qtyInp.value, 10) || 1 : 1;
          if (id > 0) items.push({ id: id, quantity: qty });
        });
        if (!items.length) return;
        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var body = { items: items };
        if (cart && cart.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }
        if (errorOut) { errorOut.hidden = true; errorOut.textContent = ''; }
        if (submit) { submit.setAttribute('aria-disabled', 'true'); submit.classList.add('loading'); var sp = submit.querySelector('.loading__spinner'); if (sp) sp.classList.remove('hidden'); }
        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            if (res.status) {
              if (errorOut) { errorOut.textContent = res.description || res.message || 'Could not add to cart.'; errorOut.hidden = false; }
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: items[0].id, cartData: res });
            }
            if (cart && cart.renderContents) {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              if (res.items && res.items.length && !res.id) {
                var main = res.items[0];
                Object.keys(main).forEach(function (k) { if (!(k in res)) res[k] = main[k]; });
              }
              cart.renderContents(res);
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch(function () { window.location = (window.routes && window.routes.cart_url) || '/cart'; })
          .finally(function () {
            if (submit) { submit.removeAttribute('aria-disabled'); submit.classList.remove('loading'); var sp = submit.querySelector('.loading__spinner'); if (sp) sp.classList.add('hidden'); }
          });
      });
    });
  }

  /* ---------- 7. read aloud (Web Speech API; BUILD-xlviii) ----------
     Hidden until speechSynthesis and an English voice exist. Never autoplays. One utterance per
     sentence (Chromium stops long utterances; Safari has no reliable onboundary), so the current
     block gets .ls-read__now and scrolls into view. Tables, cards, kit cards, the tool, chip strips and
     figures are skipped; each table is announced once. Per-section buttons are injected after every
     h2[id]. State goes to a role=status region; focus returns to the play button on stop. */
  function initReadAloud() {
    var root = document.querySelector('[data-ls-read]');
    var body = document.querySelector('.ls-guide__body');
    if (!root || !body || !('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) return;
    var synth = window.speechSynthesis;
    var SKIP = '.ls-guide__tablewrap, .ls-gcards, .ls-gcard, .ls-kit, .ls-guide__strip, [data-guide-byos], .ls-guide__table-hint, .ls-fig, .ls-photo, details.ls-guide__toggle, [data-guide-narration], .ls-kit, .ls-kitbtn, script, template, .ls-read__section';
    var queue = [], idx = -1, voice = null, playing = false;
    var status = root.querySelector('[data-ls-read-status]');
    var rateSel = root.querySelector('[data-ls-read-rate]');
    var stopBtn = root.querySelector('[data-ls-read-stop]');
    var playBtn = root.querySelector('[data-ls-read-play]');
    var label = playBtn.querySelector('[data-ls-read-label]');
    var PREFER = ['Google UK English Male', 'Google UK English Female', 'Daniel', 'Arthur', 'Kate', 'Microsoft Ryan Online (Natural) - English (United Kingdom)', 'Microsoft Sonia Online (Natural) - English (United Kingdom)', 'Microsoft Libby Online (Natural) - English (United Kingdom)', 'Ryan', 'Sonia', 'Libby'];
    function pickVoice() {
      var vs = synth.getVoices() || [];
      voice = null;
      for (var i = 0; i < PREFER.length && !voice; i++) {
        voice = vs.filter(function (v) { return v.name === PREFER[i] || (v.name.indexOf(PREFER[i]) === 0 && /en[-_]GB/i.test(v.lang)); })[0] || null;
      }
      voice = voice || vs.filter(function (v) { return /en[-_]GB/i.test(v.lang); })[0] || vs.filter(function (v) { return /^en/i.test(v.lang); })[0] || null;
      if (voice || vs.length === 0) root.hidden = false;
      else root.hidden = true;
    }
    /* £45.98 -> "forty-five pounds ninety-eight"; 12.5 kg -> "twelve point five kilograms" */
    var ONES = ['zero','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
    var TENS = ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];
    function words(n) {
      n = Math.floor(n);
      if (n < 20) return ONES[n];
      if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? '-' + ONES[n % 10] : '');
      if (n < 1000) return ONES[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + words(n % 100) : '');
      return words(Math.floor(n / 1000)) + ' thousand' + (n % 1000 ? (n % 1000 < 100 ? ' and ' : ' ') + words(n % 1000) : '');
    }
    function speakable(text) {
      return text
        .replace(/£\s?(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d{2}))?/g, function (m, p, pen) {
          var pounds = parseInt(p.replace(/,/g, ''), 10); var out = words(pounds) + (pounds === 1 ? ' pound' : ' pounds');
          if (pen && pen !== '00') out += ' ' + words(parseInt(pen, 10));
          return out;
        })
        .replace(/(\d+(?:\.\d+)?)\s?kg\b/g, function (m, n) { var parts = n.split('.'); return (parts[1] ? words(parseInt(parts[0], 10)) + ' point ' + parts[1].split('').map(function (d) { return ONES[parseInt(d, 10)]; }).join(' ') : words(parseInt(parts[0], 10))) + ' kilograms'; })
        .replace(/\s×\s/g, ' by ')
        .replace(/\bA3\b/g, 'A three').replace(/\bA5\b/g, 'A five').replace(/\bA6\b/g, 'A six').replace(/\bA10\b/g, 'A ten').replace(/\bL3\b/g, 'L three');
    }
    function narrationText(node) {
      var c = node.cloneNode(true);
      c.querySelectorAll('.visually-hidden').forEach(function (h) { h.parentNode.removeChild(h); });
      return c.textContent.replace(/\s+/g, ' ').trim();
    }
    pickVoice();
    if (typeof synth.addEventListener === 'function') synth.addEventListener('voiceschanged', pickVoice);
    try { var saved = JSON.parse(localStorage.getItem('ls-reading') || '{}'); if (saved.rate) rateSel.value = String(saved.rate); } catch (e) {}
    function segment() {
      if (body.hasAttribute('data-ls-segmented')) return;
      body.setAttribute('data-ls-segmented', '');
      body.querySelectorAll('h2, h3, h4, p, li, blockquote, summary').forEach(function (el) {
        if (el.closest(SKIP)) return;
        if (el.tagName === 'LI' && el.closest('details')) return;
        if (el.textContent.replace(/\s+/g, ' ').trim()) el.setAttribute('data-ls-say', '');
      });
      body.querySelectorAll('.ls-guide__tablewrap').forEach(function (w) {
        var cap = w.querySelector('caption');
        var note = document.createElement('span');
        note.className = 'visually-hidden';
        note.setAttribute('data-ls-say', '');
        note.textContent = 'Table skipped: ' + (cap ? cap.textContent.trim() : 'see the table on the page') + '.';
        w.parentNode.insertBefore(note, w);
      });
    }
    function pushSentences(el, text) {
      speakable(text).replace(/([.!?])\s+(?=[A-Z0-9"'(])/g, '$1\u0001').split('\u0001').forEach(function (s) { if (s) queue.push({ el: el, text: s }); });
    }
    function build(fromId) {
      segment();
      queue = [];
      var heads = Array.prototype.slice.call(body.querySelectorAll('h2[id]'));
      var narr = {};
      body.querySelectorAll('[data-guide-narration]').forEach(function (n) { narr[n.getAttribute('data-guide-narration')] = n; });
      var lead = body.querySelector('[data-guide-narration="lead"]');
      if (!fromId && lead) pushSentences(body.querySelector('.ls-tldr') || lead, narrationText(lead));
      else if (!fromId) {
        body.querySelectorAll('[data-ls-say]').forEach(function (el) {
          if (el.tagName === 'H2') return;
          if (heads.length && (el.compareDocumentPosition(heads[0]) & Node.DOCUMENT_POSITION_FOLLOWING)) pushSentences(el, el.textContent);
        });
      }
      heads.forEach(function (h, i) {
        if (fromId && h.id !== fromId) return;
        var n = narr[h.id] || narr[h.getAttribute('data-narration-key') || ''];
        pushSentences(h, h.textContent);
        if (n) { pushSentences(h, narrationText(n)); return; }
        var end = heads[i + 1];
        body.querySelectorAll('[data-ls-say]').forEach(function (el) {
          if (el.tagName === 'H2') return;
          if (!(h.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
          if (end && (el.compareDocumentPosition(end) & Node.DOCUMENT_POSITION_PRECEDING)) return;
          pushSentences(el, el.textContent);
        });
      });
    }
    function minutes() {
      var words = queue.reduce(function (n, q) { return n + q.text.split(' ').length; }, 0);
      return Math.max(1, Math.round(words / (150 * (parseFloat(rateSel.value) || 1))));
    }
    function clearNow() { body.querySelectorAll('.ls-read__now').forEach(function (n) { n.classList.remove('ls-read__now'); }); }
    function say(i) {
      if (i >= queue.length) return stop('Finished reading.');
      idx = i;
      var item = queue[i];
      var u = new SpeechSynthesisUtterance(item.text);
      u.lang = 'en-GB';
      if (voice) u.voice = voice;
      u.rate = parseFloat(rateSel.value) || 0.94;
      u.pitch = 0.98;
      u.onstart = function () {
        clearNow();
        item.el.classList.add('ls-read__now');
        var r = item.el.getBoundingClientRect();
        if (r.top < headerH() || r.bottom > window.innerHeight) item.el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      };
      u.onend = function () { if (playing) window.setTimeout(function () { if (playing) say(i + 1); }, 350); };
      u.onerror = function (ev) { if (ev.error !== 'interrupted' && ev.error !== 'canceled') stop('Read-aloud stopped: your browser could not continue.'); };
      synth.speak(u);
    }
    function setPressed(btn) {
      document.querySelectorAll('[data-ls-read-play], [data-ls-read-from]').forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
    }
    function play(fromId, btn) {
      synth.cancel(); build(fromId); playing = true; setPressed(btn);
      stopBtn.hidden = false; label.textContent = 'Pause';
      var h = fromId ? document.getElementById(fromId) : null;
      status.textContent = (h ? 'Reading from ' + h.textContent.trim() : 'Reading the whole guide') + ', about ' + minutes() + ' minutes.';
      say(0);
    }
    function stop(msg) {
      playing = false; synth.cancel(); idx = -1; setPressed(null); clearNow();
      stopBtn.hidden = true; label.textContent = 'Listen to this guide';
      status.textContent = msg || 'Stopped.';
      playBtn.focus({ preventScroll: true });
    }
    playBtn.addEventListener('click', function () {
      if (!playing) return play(null, playBtn);
      if (synth.paused) { synth.resume(); status.textContent = 'Resumed.'; label.textContent = 'Pause'; }
      else { synth.pause(); status.textContent = 'Paused.'; label.textContent = 'Resume'; }
    });
    stopBtn.addEventListener('click', function () { stop(); });
    rateSel.addEventListener('change', function () {
      try { var s = JSON.parse(localStorage.getItem('ls-reading') || '{}'); s.rate = parseFloat(rateSel.value); localStorage.setItem('ls-reading', JSON.stringify(s)); } catch (e) {}
      if (playing && idx >= 0) { synth.cancel(); say(idx); }
    });
    body.querySelectorAll('h2[id]').forEach(function (h) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'ls-read__section';
      b.setAttribute('data-ls-read-from', h.id); b.setAttribute('aria-pressed', 'false');
      b.innerHTML = '<svg aria-hidden="true" viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8v4h3l4 3V5L6 8H3z"/><path d="M13 7.5a3.5 3.5 0 0 1 0 5"/></svg> Listen to this section';
      h.insertAdjacentElement('afterend', b);
      b.addEventListener('click', function () { (playing && b.getAttribute('aria-pressed') === 'true') ? stop() : play(h.id, b); });
    });
    document.addEventListener('visibilitychange', function () { if (document.hidden && playing && !synth.paused) synth.pause(); });
    window.addEventListener('pagehide', function () { synth.cancel(); });
  }

  /* ---------- 8. reading options (text size, spacing, dyslexia-friendly; BUILD-xlviii) ---------- */
  function initReadingOptions() {
    var form = document.querySelector('[data-ls-opts-form]');
    if (!form) return;
    var html = document.documentElement;
    var state = {};
    try { state = JSON.parse(localStorage.getItem('ls-reading') || '{}'); } catch (e) { state = {}; }
    function reflect() {
      var size = form.querySelector('input[name="ls-size"][value="' + (state.size || 0) + '"]'); if (size) size.checked = true;
      var space = form.querySelector('input[name="ls-space"][value="' + (state.space || 0) + '"]'); if (space) space.checked = true;
      var dys = form.querySelector('input[name="ls-dys"]'); if (dys) dys.checked = !!state.dys;
    }
    function apply() {
      if (state.size) html.setAttribute('data-ls-size', state.size); else html.removeAttribute('data-ls-size');
      if (state.space) html.setAttribute('data-ls-space', state.space); else html.removeAttribute('data-ls-space');
      if (state.dys) html.setAttribute('data-ls-dys', ''); else html.removeAttribute('data-ls-dys');
      try { localStorage.setItem('ls-reading', JSON.stringify(state)); } catch (e) {}
    }
    reflect();
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'ls-size') state.size = parseInt(t.value, 10) || 0;
      if (t.name === 'ls-space') state.space = parseInt(t.value, 10) || 0;
      if (t.name === 'ls-dys') state.dys = !!t.checked;
      apply();
    });
    var reset = form.querySelector('[data-ls-opts-reset]');
    if (reset) reset.addEventListener('click', function () { var rate = state.rate; state = {}; if (rate) state.rate = rate; reflect(); apply(); });
  }

  function init() {
    initOutline();
    initTables();
    initShopBar();
    initOfferBar();
    initBuildYourSet();
    initShare();
    initMultiAdd();
    initReadAloud();
    initReadingOptions();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
